import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ISRPredisposition } from "@gloria-public/home/domain/interfaces/home.interface";
import { ServiceRequestOrderStatus } from "@gloria-public/order/domain/interfaces/cart.interface";
import { TranslateService } from "@ngx-translate/core";
import { ServiceRequestDialogComponent } from "@shared/components/service-request-dialog/service-request-dialog.component";
import { IResultSerializable } from "@shared/interfaces/service-requests.interface";
import { ServiceRequestService } from "@shared/services/service-requests.service";
import { Subject, first, takeUntil } from "rxjs";

@Component({
  selector: "app-sr-predisposition",
  templateUrl: "./sr-predisposition.component.html",
  styleUrls: ["./sr-predisposition.component.scss"],
})
export class SRPredispositionComponent implements OnInit, OnDestroy {
  @Input() srpredisposition!: ISRPredisposition;
  @Input() riskId!: string;

  result?: IResultSerializable;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog,
    private serviceRequestService: ServiceRequestService,
    public snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.result = undefined;

    this.serviceRequestService
      .getServiceRequestWithResults(this.srpredisposition.serviceRequest, this.translateService.currentLang)
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((response) => {
        this.result = response.results?.[0];
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Open service request in big
   */
  public openServiceRequest(): void {
    this.serviceRequestService
      .getServiceRequestWithResults(this.srpredisposition.serviceRequest, this.translateService.currentLang)
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe({
        next: (serviceRequest) => {
          const paidUntil = this.srpredisposition.paidUntil ? new Date(this.srpredisposition.paidUntil) : null;
          const now = new Date();
          const alreadyBought = this.srpredisposition.status !== ServiceRequestOrderStatus.NOT_ORDERED;
          const canPurchase = !alreadyBought || (paidUntil ? paidUntil <= now : true);

          const dialogRef = this.dialog.open(ServiceRequestDialogComponent, {
            disableClose: true,
            data: {
              response: serviceRequest.template,
              results: serviceRequest.results,
              riskId: this.riskId,
              canPurchase: canPurchase,
              alreadyBought: alreadyBought,
              paymentStatus: this.srpredisposition.paymentStatus,
              serviceRequestId: this.srpredisposition.requestInOrder,
              paidUntil: paidUntil,
            },
            width: "90vw",
            maxWidth: "600px",
            maxHeight: "90vh",
          });

          dialogRef.componentInstance.allResultsSeen.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            if (this.srpredisposition.hasUnseenResult) {
              this.srpredisposition.hasUnseenResult = false;
            }
          });

          // In case it changed to cancelled
          dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
              if (result?.paymentStatus) {
                this.srpredisposition.paymentStatus = result.paymentStatus;
              }
            });
        },

        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        },
      });
  }
}
